import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Building2, UserCircle2, CheckCircle2, Save, ImageIcon, Plus, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import ImageUploader from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Tecnologia", "Design", "Marketing", "Reformas", "Ensino", "Serviços Gerais", "Delivery", "Outros"];

export default function MyWorkProfile() {
  const [mode, setMode] = useState("freelancer"); // freelancer | company
  const [f, setF] = useState({ headline: "", bio: "", skills: [], categories: [], languages: [], experience: [], availability: "full", city: "", contact_email: "", contact_phone: "", portfolio_url: "", online: false, portfolio: [], address: { street: "", number: "", complement: "", neighborhood: "", city: "", state: "", cep: "" } });
  const [c, setC] = useState({ company_name: "", cnpj: "", industry: "", description: "", website: "", city: "", logo_url: "", name_visible: true });
  const [freelancerResume, setFreelancerResume] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [skillsInput, setSkillsInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [fRes, cRes, iRes] = await Promise.all([
        axios.get(`${API}/freelancers/me`),
        axios.get(`${API}/companies/me`),
        axios.get(`${API}/freelancers/invites/me`).catch(() => ({ data: [] })),
      ]);
      if (fRes.data.has_profile) {
        setF({ ...f, ...fRes.data.profile });
        setSkillsInput((fRes.data.profile.skills || []).join(", "));
        setFreelancerResume(fRes.data.resume);
      }
      if (cRes.data.has_profile) {
        setC({ ...c, ...cRes.data.profile });
        setCompanyData(cRes.data);
      }
      setInvites(iRes.data);
    } catch (err) { console.warn("[MyWorkProfile] load failed", err?.message); }
    // eslint-disable-next-line
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  const saveFreelancer = async () => {
    if (!f.headline?.trim()) return toast.error("Informe uma headline");
    setSaving(true);
    try {
      const skills = skillsInput.split(",").map(s => s.trim()).filter(Boolean);
      const payload = {
        ...f, skills,
        experience: (f.experience || []).filter(e => e && e.role),
        address: f.address || {},
      };
      delete payload.portfolio; // saved via dedicated endpoint
      const r = await axios.post(`${API}/freelancers/me`, payload);
      // Persist portfolio separately
      if (Array.isArray(f.portfolio)) {
        await axios.put(`${API}/freelancers/me/portfolio`, {
          items: f.portfolio.filter(i => i && i.title).map(i => ({
            title: i.title, description: i.description || "", url: i.url || "", image_url: i.image_url || "",
          })),
        }).catch(() => {});
      }
      setFreelancerResume(r.data.resume);
      toast.success("Currículo salvo! Empresas podem convidar você.");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const saveCompany = async () => {
    if (!c.company_name?.trim()) return toast.error("Informe o nome da empresa");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/companies/me`, c);
      setCompanyData({ has_profile: true, profile: r.data.profile, active_subscription: companyData?.active_subscription });
      toast.success("Perfil da empresa salvo!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  const respondInvite = async (id, action) => {
    try {
      await axios.put(`${API}/freelancers/invites/${id}`, { action });
      toast.success(action === "accept" ? "Convite aceito!" : "Convite recusado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          data-testid="mode-freelancer"
          onClick={() => setMode("freelancer")}
          className={`px-4 py-2 rounded-full text-sm font-medium border-2 flex items-center gap-2 transition ${mode === "freelancer" ? "border-primary bg-primary/10" : "border-border"}`}
        >
          <UserCircle2 className="w-4 h-4" /> Sou freelancer
        </button>
        <button
          data-testid="mode-company"
          onClick={() => setMode("company")}
          className={`px-4 py-2 rounded-full text-sm font-medium border-2 flex items-center gap-2 transition ${mode === "company" ? "border-primary bg-primary/10" : "border-border"}`}
        >
          <Building2 className="w-4 h-4" /> Sou empresa/agência
        </button>
      </div>

      {mode === "freelancer" && (
        <>
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <UserCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-tight">Cadastro de freelancer</h2>
                <p className="text-xs text-muted-foreground">Preencha para gerar seu currículo automaticamente e receber convites de empresas.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs">Headline (título profissional)</Label>
                <Input data-testid="fl-headline" placeholder="Ex: Desenvolvedor Full-stack com 5 anos" value={f.headline} onChange={(e) => setF({ ...f, headline: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Bio / apresentação</Label>
                <Textarea data-testid="fl-bio" rows={3} placeholder="Conte um pouco sobre você..." value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Skills (separadas por vírgula)</Label>
                <Input data-testid="fl-skills" placeholder="React, Node.js, Design UX" value={skillsInput} onChange={(e) => setSkillsInput(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">Cidade</Label>
                <Input data-testid="fl-city" placeholder="São Paulo" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Disponibilidade</Label>
                <select data-testid="fl-availability" value={f.availability} onChange={(e) => setF({ ...f, availability: e.target.value })} className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm">
                  <option value="full">Integral</option>
                  <option value="part">Parcial</option>
                  <option value="freelance">Freelance</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">E-mail de contato (opcional)</Label>
                <Input data-testid="fl-contact-email" type="email" placeholder="voce@email.com" value={f.contact_email || ""} onChange={(e) => setF({ ...f, contact_email: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Telefone de contato (opcional)</Label>
                <Input data-testid="fl-contact-phone" placeholder="(11) 9..." value={f.contact_phone || ""} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Portfólio (URL)</Label>
                <Input data-testid="fl-portfolio" placeholder="https://..." value={f.portfolio_url || ""} onChange={(e) => setF({ ...f, portfolio_url: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Categorias (múltipla)</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {CATEGORIES.map(cat => {
                    const on = f.categories?.includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        data-testid={`fl-cat-${cat}`}
                        onClick={() => setF({ ...f, categories: on ? f.categories.filter(x => x !== cat) : [...(f.categories || []), cat] })}
                        className={`text-[10px] px-2 py-1 rounded-full border ${on ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                      >
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Feature #21: Address */}
              <div className="md:col-span-2 p-3 rounded-lg border border-dashed border-border space-y-2" data-testid="fl-address-block">
                <div className="text-sm font-semibold">Endereço completo</div>
                <div className="grid grid-cols-6 gap-2">
                  <Input className="col-span-4" data-testid="fl-addr-street" placeholder="Rua / Av." value={f.address?.street || ""} onChange={(e) => setF({ ...f, address: { ...(f.address || {}), street: e.target.value } })} />
                  <Input className="col-span-1" data-testid="fl-addr-number" placeholder="Nº" value={f.address?.number || ""} onChange={(e) => setF({ ...f, address: { ...(f.address || {}), number: e.target.value } })} />
                  <Input className="col-span-1" data-testid="fl-addr-cep" placeholder="CEP" value={f.address?.cep || ""} onChange={(e) => setF({ ...f, address: { ...(f.address || {}), cep: e.target.value } })} />
                  <Input className="col-span-3" data-testid="fl-addr-neigh" placeholder="Bairro" value={f.address?.neighborhood || ""} onChange={(e) => setF({ ...f, address: { ...(f.address || {}), neighborhood: e.target.value } })} />
                  <Input className="col-span-2" data-testid="fl-addr-city" placeholder="Cidade" value={f.address?.city || ""} onChange={(e) => setF({ ...f, address: { ...(f.address || {}), city: e.target.value } })} />
                  <Input className="col-span-1" data-testid="fl-addr-state" placeholder="UF" value={f.address?.state || ""} onChange={(e) => setF({ ...f, address: { ...(f.address || {}), state: e.target.value } })} />
                </div>
              </div>

              {/* Feature #21: Experiences */}
              <div className="md:col-span-2 p-3 rounded-lg border border-dashed border-border" data-testid="fl-experience-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold">Experiências profissionais</div>
                  <Button size="sm" data-testid="fl-add-experience" type="button" onClick={() => setF({ ...f, experience: [...(f.experience || []), { _uid: `exp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, role: "", company: "", start: "", end: "", description: "" }] })} className="rounded-full h-7 text-xs">+ adicionar</Button>
                </div>
                <div className="space-y-2">
                  {(f.experience || []).length === 0 && <p className="text-xs text-muted-foreground">Nenhuma experiência ainda.</p>}
                  {(f.experience || []).map((exp, i) => (
                    <div key={exp._uid || `exp-legacy-${i}`} className="p-3 rounded-lg border border-border space-y-2" data-testid={`fl-exp-row-${i}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <Input data-testid={`fl-exp-role-${i}`} placeholder="Cargo / função" value={exp.role || ""} onChange={(e) => setF({ ...f, experience: f.experience.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x) })} />
                        <Input data-testid={`fl-exp-company-${i}`} placeholder="Empresa" value={exp.company || ""} onChange={(e) => setF({ ...f, experience: f.experience.map((x, idx) => idx === i ? { ...x, company: e.target.value } : x) })} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input data-testid={`fl-exp-start-${i}`} placeholder="Início (2022-01)" value={exp.start || ""} onChange={(e) => setF({ ...f, experience: f.experience.map((x, idx) => idx === i ? { ...x, start: e.target.value } : x) })} />
                        <Input data-testid={`fl-exp-end-${i}`} placeholder="Fim (vazio = atual)" value={exp.end || ""} onChange={(e) => setF({ ...f, experience: f.experience.map((x, idx) => idx === i ? { ...x, end: e.target.value } : x) })} />
                      </div>
                      <Textarea data-testid={`fl-exp-desc-${i}`} rows={2} placeholder="Descrição breve do que fez" value={exp.description || ""} onChange={(e) => setF({ ...f, experience: f.experience.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x) })} />
                      <Button size="sm" type="button" variant="ghost" onClick={() => setF({ ...f, experience: f.experience.filter((_, idx) => idx !== i) })} data-testid={`fl-exp-remove-${i}`} className="text-destructive h-7">Remover</Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Feature #22: online toggle */}
              <div className="md:col-span-2 p-3 rounded-lg border border-dashed border-primary/30 space-y-3" data-testid="fl-portfolio-block">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-primary" /> Portfólio</div>
                  <Button size="sm" data-testid="fl-add-portfolio" type="button" onClick={() => setF({ ...f, portfolio: [...(f.portfolio || []), { title: "", description: "", url: "", image_url: "" }] })} className="rounded-full h-7 text-xs gap-1">
                    <Plus className="w-3 h-3" /> adicionar item
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Mostre até 20 trabalhos anteriores para conquistar clientes.</p>
                {(f.portfolio || []).length === 0 && <p className="text-xs text-muted-foreground text-center py-2">Nenhum item de portfólio ainda.</p>}
                <div className="grid sm:grid-cols-2 gap-2">
                  {(f.portfolio || []).map((p, i) => (
                    <div key={i} className="p-3 rounded-lg border border-border bg-background/60 space-y-2" data-testid={`fl-portfolio-item-${i}`}>
                      <div className="flex items-start justify-between gap-2">
                        <Input data-testid={`fl-portfolio-title-${i}`} placeholder="Título do trabalho" value={p.title || ""} onChange={(e) => setF({ ...f, portfolio: f.portfolio.map((x, idx) => idx === i ? { ...x, title: e.target.value } : x) })} className="text-sm font-semibold" />
                        <button type="button" onClick={() => setF({ ...f, portfolio: f.portfolio.filter((_, idx) => idx !== i) })} data-testid={`fl-portfolio-remove-${i}`} className="text-destructive hover:opacity-70">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <Textarea data-testid={`fl-portfolio-desc-${i}`} rows={2} placeholder="Descrição curta" value={p.description || ""} onChange={(e) => setF({ ...f, portfolio: f.portfolio.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x) })} />
                      <Input data-testid={`fl-portfolio-url-${i}`} placeholder="Link (opcional)" value={p.url || ""} onChange={(e) => setF({ ...f, portfolio: f.portfolio.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x) })} />
                      <div className="flex items-center gap-2">
                        <Input data-testid={`fl-portfolio-img-${i}`} placeholder="Imagem (URL)" value={p.image_url || ""} onChange={(e) => setF({ ...f, portfolio: f.portfolio.map((x, idx) => idx === i ? { ...x, image_url: e.target.value } : x) })} className="flex-1" />
                        <ImageUploader testid={`fl-portfolio-upload-${i}`} label="Enviar" size="sm" variant="outline" accept="image/*" onUploaded={(url) => setF({ ...f, portfolio: f.portfolio.map((x, idx) => idx === i ? { ...x, image_url: url } : x) })} />
                      </div>
                      {p.image_url && <img src={p.image_url} alt="" className="w-full h-24 object-cover rounded" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Feature #22: online toggle */}
              <div className="md:col-span-2 flex items-center justify-between p-3 rounded-lg border border-primary/40 bg-primary/5" data-testid="fl-online-block">
                <div>
                  <div className="font-semibold text-sm flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${f.online ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                    {f.online ? "Você está ONLINE no mapa" : "Você está offline"}
                  </div>
                  <div className="text-xs text-muted-foreground">Ative para aparecer no mapa em tempo real e receber chamadas.</div>
                </div>
                <Switch data-testid="fl-online-toggle" checked={!!f.online} onCheckedChange={async (v) => {
                  setF({ ...f, online: v });
                  try {
                    const geo = v && navigator.geolocation
                      ? await new Promise((res) => navigator.geolocation.getCurrentPosition(
                          (pos) => res({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                          () => res({ lat: null, lng: null }),
                        ))
                      : { lat: null, lng: null };
                    await axios.post(`${API}/freelancers/online`, { online: v, lat: geo.lat, lng: geo.lng });
                    toast.success(v ? "Você está online no mapa" : "Você saiu do mapa");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Salve seu perfil primeiro");
                    setF({ ...f, online: !v });
                  }
                }} />
              </div>
            </div>

            <Button data-testid="fl-save" onClick={saveFreelancer} disabled={saving} className="rounded-full gap-1 mt-4">
              <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar currículo"}
            </Button>
          </Card>

          {freelancerResume && (
            <Card className="p-6 bg-gradient-to-br from-primary/5 to-transparent border-primary/30" data-testid="fl-resume-preview">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <h3 className="text-lg font-bold tracking-tight">Currículo gerado automaticamente</h3>
              </div>
              <div className="flex items-start gap-4">
                {freelancerResume.picture && <img src={freelancerResume.picture} alt="" className="w-16 h-16 rounded-xl object-cover" />}
                <div className="flex-1">
                  <div className="text-xl font-bold">{freelancerResume.name}</div>
                  <div className="text-sm text-muted-foreground">{freelancerResume.headline}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {freelancerResume.email} · {freelancerResume.phone || "sem telefone"} · {freelancerResume.city}
                  </div>
                  {freelancerResume.rating_avg && (
                    <div className="text-xs mt-1">⭐ {freelancerResume.rating_avg.toFixed(1)} ({freelancerResume.rating_count} avaliações)</div>
                  )}
                  <p className="text-sm mt-2">{freelancerResume.bio}</p>
                  {freelancerResume.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {freelancerResume.skills.map((s, i) => <Badge key={i} variant="secondary" className="text-[10px]">{s}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          )}

          {invites.length > 0 && (
            <Card className="p-6" data-testid="fl-invites-list">
              <h3 className="text-lg font-bold tracking-tight mb-3">Convites recebidos ({invites.length})</h3>
              <div className="space-y-2">
                {invites.map(inv => (
                  <div key={inv.invite_id} className="p-3 rounded-lg border border-border" data-testid={`invite-${inv.invite_id}`}>
                    <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                      <div>
                        <div className="font-bold text-sm">{inv.title}</div>
                        <div className="text-xs text-muted-foreground">De: <strong>{inv.sender_label}</strong> · R$ {Number(inv.budget || 0).toFixed(2)}</div>
                        {Number(inv.call_price) > 0 && <div className="text-xs font-semibold text-emerald-600" data-testid={`invite-call-price-${inv.invite_id}`}>Chamada paga: R$ {Number(inv.call_price).toFixed(2)} {inv.escrow_status === "released" ? "· liberado na sua carteira" : inv.escrow_status === "refunded" ? "· devolvido ao cliente" : "· garantido em escrow"}</div>}
                      </div>
                      <Badge variant={inv.status === "sent" ? "secondary" : inv.status === "accepted" ? "default" : "destructive"}>{inv.status}</Badge>
                    </div>
                    <p className="text-sm mt-2">{inv.description}</p>
                    {(inv.contact_email || inv.contact_phone) && (
                      <p className="text-xs text-muted-foreground mt-1">Contato: {inv.contact_email} {inv.contact_phone}</p>
                    )}
                    {inv.status === "sent" && (
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" onClick={() => respondInvite(inv.invite_id, "accept")} data-testid={`invite-accept-${inv.invite_id}`}>Aceitar</Button>
                        <Button size="sm" variant="outline" onClick={() => respondInvite(inv.invite_id, "reject")} data-testid={`invite-reject-${inv.invite_id}`}>Recusar</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      {mode === "company" && (
        <>
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold tracking-tight">Cadastro de empresa/agência</h2>
                <p className="text-xs text-muted-foreground">Cadastre sua empresa para publicar vagas e convidar freelancers.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Nome da empresa</Label>
                <Input data-testid="co-name" placeholder="Ex: Acme Tecnologia" value={c.company_name} onChange={(e) => setC({ ...c, company_name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">CNPJ</Label>
                <Input data-testid="co-cnpj" placeholder="00.000.000/0000-00" value={c.cnpj} onChange={(e) => setC({ ...c, cnpj: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Setor</Label>
                <Input data-testid="co-industry" placeholder="Ex: TI, Marketing..." value={c.industry} onChange={(e) => setC({ ...c, industry: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Cidade</Label>
                <Input data-testid="co-city" placeholder="Cidade sede" value={c.city} onChange={(e) => setC({ ...c, city: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Website</Label>
                <Input data-testid="co-website" placeholder="https://..." value={c.website} onChange={(e) => setC({ ...c, website: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Logo (URL)</Label>
                <Input data-testid="co-logo" placeholder="https://..." value={c.logo_url} onChange={(e) => setC({ ...c, logo_url: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Descrição</Label>
                <Textarea data-testid="co-description" rows={3} value={c.description} onChange={(e) => setC({ ...c, description: e.target.value })} />
              </div>
              <div className="md:col-span-2 flex items-center justify-between p-3 rounded-lg border border-border" data-testid="co-name-visibility">
                <div>
                  <div className="font-semibold text-sm">Nome visível para candidatos</div>
                  <div className="text-xs text-muted-foreground">Ative para exibir o nome. Desative para publicar como "Empresa confidencial".</div>
                </div>
                <Switch data-testid="co-name-visible" checked={c.name_visible} onCheckedChange={(v) => setC({ ...c, name_visible: v })} />
              </div>
            </div>

            <Button data-testid="co-save" onClick={saveCompany} disabled={saving} className="rounded-full gap-1 mt-4">
              <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar empresa"}
            </Button>
          </Card>

          {companyData?.active_subscription ? (
            <Card className="p-4 bg-green-500/10 border-green-500/40 text-sm" data-testid="co-active-sub">
              ✓ Plano ativo: <strong>{companyData.active_subscription.plan_name}</strong> · válido até {new Date(companyData.active_subscription.expires_at).toLocaleDateString()}
            </Card>
          ) : (
            companyData?.has_profile && (
              <Card className="p-4 bg-orange-500/10 border-orange-500/40 text-sm" data-testid="co-no-sub">
                ⚠ Sua empresa não tem plano ativo. Vá até a aba <strong>Planos</strong> para assinar e liberar publicação de vagas.
              </Card>
            )
          )}
        </>
      )}
    </div>
  );
}
